'use client'

import type { CreateConnectorFn } from 'wagmi'

import { version } from '@wagmi/core'
import { tempoWallet } from 'wagmi/tempo'

import { logger } from '@/lib/observability/logger'

type PersistedStore = {
  state: { connections?: Map<string, { connector: { id: string } }> }
  version: number
}

const storeVersion = Number.parseInt(version.split('.')[0] ?? '0', 10)

const isPersistedStore = (value: unknown): value is PersistedStore =>
  typeof value === 'object' &&
  value !== null &&
  'state' in value &&
  typeof value.state === 'object' &&
  value.state !== null &&
  'connections' in value.state &&
  value.state.connections instanceof Map &&
  'version' in value &&
  value.version === storeVersion

/**
 * Tempo's `accounts` provider appends the wallet.tempo.xyz/embed iframe at
 * `Provider.create`, and the connector's `setup()` eagerly awaits `getProvider()`,
 * so an unwrapped connector mounts the dialog (~1 MB JS) for every visitor. This
 * wrapper relies on wagmi `reconnect` skipping falsy providers and on AppKit's
 * `syncConnectors` swallowing `getProvider()` rejections. It also assumes nothing
 * calls `getChainId`/`getClient`/`disconnect`/`switchChain` on a disconnected
 * connector, since those are spread through un-gated and each awaits
 * `getProvider()` upstream. Persisted state is only trusted when its `version`
 * matches wagmi's store version, matching wagmi's own `migrate` reset.
 *
 * Remove once `@wagmi/core`'s Tempo connector creates its provider lazily.
 */
export function lazyTempoWallet(): CreateConnectorFn {
  return config => {
    const inner = tempoWallet()(config)
    let activated = false

    const activate = () => {
      if (!activated) {
        activated = true
        inner
          .setup?.()
          .catch(error => logger.warn('Tempo connector setup failed', { extra: { cause: error } }))
      }
      return inner
    }

    const hasPersistedTempoConnection = async () => {
      try {
        const persisted = await config.storage?.getItem('store')
        const connections = isPersistedStore(persisted) ? persisted.state.connections : undefined
        return [...(connections?.values() ?? [])].some(({ connector }) => connector.id === inner.id)
      } catch {
        return false
      }
    }

    return {
      ...inner,
      async setup() {
        if (await hasPersistedTempoConnection()) activate()
      },
      async getProvider(params) {
        if (!activated && (await hasPersistedTempoConnection())) activate()
        return activated ? inner.getProvider(params) : undefined
      },
      async connect(params) {
        return activate().connect(params)
      },
      async isAuthorized() {
        return activated ? inner.isAuthorized() : false
      },
      async getAccounts() {
        return activated ? inner.getAccounts() : []
      }
    }
  }
}
